import api from './api';

export enum BillType {
  INCOME = 'INCOME',
  OUTCOME = 'OUTCOME'
}

export enum BillStatus {
  PENDING = 'PENDING',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  PAID = 'PAID',
  CANCELLED = 'CANCELLED',
  OVERDUE = 'OVERDUE'
}

export enum ResourceType {
  TEACHER = 'TEACHER',
  STUDENT = 'STUDENT',
  ADMIN = 'ADMIN'
}

export interface Payment {
  paymentDate: string;
  amount: number;
  note?: string;
}

export interface Bill {
  id: string;
  schoolId: string;
  billType: BillType;
  status: BillStatus;
  amount: number;
  resourceType: ResourceType;
  resourceId: string;
  resourceName?: string; // Enriched from backend
  payments: Payment[];
  creationDatetime?: string;
  totalPaid?: number; // Calculated from payments
}

export interface BillInput {
  amount: number;
  resourceType: ResourceType;
  resourceId: string;
  payments?: Payment[];
  creationDatetime?: string; // ISO string, only used on creation
  // billType and status are calculated automatically in backend
}

export interface BillSearchFilters {
  status?: BillStatus;
  billType?: BillType;
  resourceType?: ResourceType;
  resourceId?: string; // Still supported for direct links
  classId?: string;
  resourceName?: string;
  fromDate?: string; // ISO string
  toDate?: string; // ISO string
}

export interface BillSearchRequest {
  page?: number;
  size?: number;
  filters?: BillSearchFilters;
}

export interface PageResponse<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  hasNext: boolean;
}

export const billService = {
  getAllBills: async (status?: BillStatus, resourceType?: ResourceType, resourceId?: string): Promise<Bill[]> => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (resourceType) params.append('resourceType', resourceType);
    if (resourceId) params.append('resourceId', resourceId);
    
    const queryString = params.toString();
    const url = queryString ? `/{schoolId}/bills?${queryString}` : `/{schoolId}/bills`;
    const response = await api.get<Bill[]>(url);
    return response.data;
  },

  getBillsByResource: async (resourceType: ResourceType, resourceId: string): Promise<Bill[]> => {
    const response = await api.get<Bill[]>(`/{schoolId}/bills/resource/${resourceType}/${resourceId}`);
    return response.data;
  },

  searchBills: async (request: BillSearchRequest): Promise<PageResponse<Bill>> => {
    const response = await api.post<PageResponse<Bill>>(`/{schoolId}/bills/search`, request);
    return response.data;
  },

  getBillById: async (id: string): Promise<Bill> => {
    const response = await api.get<Bill>(`/{schoolId}/bills/${id}`);
    return response.data;
  },

  createBill: async (bill: BillInput): Promise<Bill> => {
    const response = await api.post<Bill>(`/{schoolId}/bills`, bill);
    return response.data;
  },

  bulkCreateBills: async (amount: number, resourceType: ResourceType, resourceIds: string[], creationDatetime?: string): Promise<Bill[]> => {
    const response = await api.post<Bill[]>(`/{schoolId}/bills/bulk`, {
      amount,
      resourceType,
      resourceIds,
      payments: [],
      creationDatetime,
    });
    return response.data;
  },

  updateBill: async (id: string, bill: BillInput): Promise<Bill> => {
    const response = await api.put<Bill>(`/{schoolId}/bills/${id}`, bill);
    return response.data;
  },

  deleteBill: async (id: string): Promise<void> => {
    await api.delete(`/{schoolId}/bills/${id}`);
  },

  addPayment: async (billId: string, payment: Payment): Promise<Bill> => {
    const response = await api.post<Bill>(`/{schoolId}/bills/${billId}/payments`, payment);
    return response.data;
  },

  updatePayment: async (billId: string, paymentIndex: number, payment: Payment): Promise<Bill> => {
    const response = await api.put<Bill>(`/{schoolId}/bills/${billId}/payments/${paymentIndex}`, payment);
    return response.data;
  },

  removePayment: async (billId: string, paymentIndex: number): Promise<Bill> => {
    const response = await api.delete<Bill>(`/{schoolId}/bills/${billId}/payments/${paymentIndex}`);
    return response.data;
  },
};

